import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import { X, Check } from 'lucide-react';
import logotransparent from '../../assets/logotransparent.png';
import loginVisual from '../../assets/loginpage.png';
import PrivacyPolicyPage from '../settings/PrivacyPolicy';
import TermsOfServicePage from '../settings/TermsOfService';

const initialState = {
  identifier: '',
  email: '',
  password: '',
  username: '',
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    user,
    initializing,
    authLoading,
    authError,
    login,
    signup,
    resetPassword,
  } = usePublicAuth();
  const [mode, setMode] = useState('signin'); // signin | signup
  const [form, setForm] = useState(initialState);
  const [statusMessage, setStatusMessage] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null); // 'terms' or 'privacy'

  useEffect(() => {
    if (!initializing && user) {
      navigate('/trips/sender', { replace: true });
    }
  }, [user, initializing, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const modeParam = params.get('mode');
    if (modeParam === 'signup' && mode !== 'signup') {
      setMode('signup');
      setStatusMessage('');
      setForm(initialState);
    } else if (modeParam === 'signin' && mode !== 'signin') {
      setMode('signin');
      setStatusMessage('');
      setForm(initialState);
    }
  }, [location.search, mode]);

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setStatusMessage('');

    if (mode === 'signin') {
      const response = await login(form.identifier, form.password);
      if (response.success) {
        setStatusMessage(t('auth.loginSuccess'));
        navigate('/trips/sender', { replace: true });
      } else if (response.error) {
        setStatusMessage(response.error);
      }
    } else {
      if (!form.username.trim()) {
        setStatusMessage(t('auth.usernameRequired'));
        return;
      }
      if (!acceptedTerms) {
        setStatusMessage(t('login.mustAcceptTerms'));
        return;
      }
      const response = await signup(form.email, form.password, form.username);
      if (response.success) {
        setStatusMessage(t('auth.signupSuccess'));
        navigate('/trips/sender', { replace: true });
      } else if (response.error) {
        setStatusMessage(response.error);
      }
    }
  };

  const handleResetPassword = async () => {
    if (!form.identifier) {
      setStatusMessage(t('auth.resetEnterEmail'));
      return;
    }
    const response = await resetPassword(form.identifier);
    if (response.success) {
      setStatusMessage(t('auth.resetEmailSent'));
    } else {
      setStatusMessage(response.error);
    }
  };

  const switchMode = () => {
    setMode(prev => (prev === 'signin' ? 'signup' : 'signin'));
    setStatusMessage('');
    setForm(initialState);
    setAcceptedTerms(false);
  };

  return (
    <div className="min-h-screen w-full text-gray-900 dark:text-slate-100">
      <div className="flex min-h-screen w-full items-center justify-center">
        <div className="w-full">
          <div className="grid min-h-screen gap-2 bg-gradient-to-br from-[#f6e9c5] via-[#fff6db] to-[#f0f5ff] dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 lg:grid-cols-[1.08fr_0.92fr]">
            <div className="relative flex h-full flex-col p-6 sm:p-10 lg:p-12">
              <div className="flex flex-col items-center justify-center text-center text-gray-600">
                <img
                  src={logotransparent}
                  alt="FlexySwap Logo"
                  className="h-16 w-16 object-contain"
                />
                <p className="mt-2 text-lg font-black tracking-[0.3em] text-gray-800 dark:text-slate-100">
                  FLEXYSWAP
                </p>
              </div>

              <div className="mt-10 space-y-3">
                <p className="text-sm font-semibold uppercase tracking-[0.4em] text-gray-500 dark:text-slate-400">
                  {mode === 'signin'
                    ? t('auth.badgeSignin')
                    : t('auth.badgeSignup')}
                </p>
                <h1 className="text-4xl font-black text-gray-900 dark:text-slate-100">
                  {mode === 'signin'
                    ? t('auth.heroSigninTitle')
                    : t('auth.heroSignupTitle')}
                </h1>
                <p className="text-sm text-gray-500 dark:text-slate-300">
                  {mode === 'signin'
                    ? t('auth.heroSigninSubtitle')
                    : t('auth.heroSignupSubtitle')}
                </p>
              </div>

              <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                {mode === 'signin' ? (
                  <>
                    <label className="block text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                      {t('auth.identifierLabel')}
                      <input
                        className="mt-3 w-full rounded-[20px] border border-gray-200/80 bg-gray-50/60 px-5 py-3 text-sm font-medium text-gray-900 transition focus:border-yellow-500 focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-100 dark:focus:border-yellow-500 dark:focus:bg-slate-700"
                        type="text"
                        name="identifier"
                        value={form.identifier}
                        onChange={handleChange}
                        placeholder={t('auth.identifierPlaceholder')}
                        autoComplete="username"
                      />
                    </label>
                  </>
                ) : (
                  <>
                    <label className="block text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                      {t('auth.emailLabel')}
                      <input
                        className="mt-3 w-full rounded-[20px] border border-gray-200/80 bg-gray-50/60 px-5 py-3 text-sm font-medium text-gray-900 transition focus:border-yellow-500 focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-100 dark:focus:border-yellow-500 dark:focus:bg-slate-700"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        autoComplete="email"
                      />
                    </label>
                    <label className="block text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                      {t('auth.usernameLabel')}
                      <input
                        className="mt-3 w-full rounded-[20px] border border-gray-200/80 bg-gray-50/60 px-5 py-3 text-sm font-medium text-gray-900 transition focus:border-yellow-500 focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-100 dark:focus:border-yellow-500 dark:focus:bg-slate-700"
                        name="username"
                        value={form.username}
                        onChange={handleChange}
                        autoComplete="off"
                      />
                    </label>
                  </>
                )}

                <label className="block text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
                  {t('auth.passwordLabel')}
                  <input
                    className="mt-3 w-full rounded-[20px] border border-gray-200/80 bg-gray-50/60 px-5 py-3 text-sm font-medium text-gray-900 transition focus:border-yellow-500 focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-100 dark:focus:border-yellow-500 dark:focus:bg-slate-700"
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    autoComplete={
                      mode === 'signin' ? 'current-password' : 'new-password'
                    }
                  />
                </label>

                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    className="text-sm font-semibold text-yellow-500 transition hover:text-yellow-400"
                    disabled={authLoading}
                  >
                    {t('auth.forgotPassword')}
                  </button>
                )}

                {/* Terms and Privacy Checkbox - Only show on Sign Up */}
                {mode === 'signup' && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={e => setAcceptedTerms(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                          acceptedTerms
                            ? 'bg-gray-900 border-gray-900'
                            : 'border-gray-300 bg-white'
                        }`}
                        onClick={() => setAcceptedTerms(!acceptedTerms)}
                      >
                        {acceptedTerms && (
                          <Check size={14} className="text-white" />
                        )}
                      </div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-slate-300 flex-1">
                      {t('login.acceptTerms')}{' '}
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          setModalContent('terms');
                          setShowModal(true);
                        }}
                        className="font-semibold text-yellow-500 underline hover:text-yellow-400 dark:text-yellow-400"
                      >
                        {t('login.termsOfService')}
                      </button>
                      {' '}{t('login.and')}{' '}
                      <button
                        type="button"
                        onClick={e => {
                          e.preventDefault();
                          setModalContent('privacy');
                          setShowModal(true);
                        }}
                        className="font-semibold text-yellow-500 underline hover:text-yellow-400 dark:text-yellow-400"
                      >
                        {t('login.privacyPolicy')}
                      </button>
                    </span>
                  </label>
                )}

                <button
                  type="submit"
                  disabled={authLoading || (mode === 'signup' && !acceptedTerms)}
                  className="w-full rounded-[26px] bg-gradient-to-r from-yellow-400 to-yellow-500 py-3.5 text-center text-base font-semibold text-gray-900 transition hover:brightness-105 disabled:opacity-60"
                >
                  {authLoading
                    ? mode === 'signin'
                      ? t('auth.signingIn')
                      : t('auth.creatingAccount')
                    : mode === 'signin'
                    ? t('auth.signinCta')
                    : t('auth.signupCta')}
                </button>

                <p className="text-center text-sm text-gray-500 dark:text-slate-400">
                  {mode === 'signin'
                    ? t('auth.noAccount')
                    : t('auth.haveAccount')}{' '}
                  <button
                    type="button"
                    onClick={switchMode}
                    className="font-semibold text-yellow-500 transition hover:text-yellow-400 dark:text-yellow-400 dark:hover:text-yellow-300"
                  >
                    {mode === 'signin'
                      ? t('auth.createOne')
                      : t('auth.signinLink')}
                  </button>
                </p>

                {(statusMessage || authError) && (
                  <p className="rounded-2xl bg-red-50 px-4 py-3 text-center text-sm text-red-600 dark:bg-red-900/30 dark:text-red-400">
                    {statusMessage || authError}
                  </p>
                )}
              </form>

              <div className="mt-10 space-y-4">
                <p className="text-center text-sm font-semibold text-gray-600 dark:text-slate-400">
                  {t('auth.downloadApp')}
                </p>
                <div className="flex items-center justify-center gap-4">
                  <div className="h-10 flex items-center">
                    <a
                      href="https://apps.apple.com/app/flexyswap"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block transition hover:opacity-80"
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                        alt="Download on the App Store"
                        className="h-10 w-auto"
                      />
                    </a>
                  </div>
                  <div className="h-10 flex items-center">
                    <a
                      href="https://play.google.com/store/apps/details?id=com.flexyswap"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block transition hover:opacity-80"
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Google_Play_Store_badge_EN.svg/512px-Google_Play_Store_badge_EN.svg.png"
                        alt="Get it on Google Play"
                        className="h-10 w-auto"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden bg-black text-white">
              <img
                src={loginVisual}
                alt="Traveler handing a parcel to a sender at the airport"
                className="h-full w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute inset-x-8 bottom-10 space-y-4 text-sm text-white">
                <div className="inline-flex rounded-full bg-yellow-400/90 px-4 py-1 text-xs font-semibold uppercase tracking-[0.4em] text-gray-900 shadow">
                  {t('footer.trustedSwaps')}
                </div>
                <h3 className="text-2xl font-black leading-tight">
                  {t('auth.sideHeroTitle')}
                </h3>
                <p className="text-white/80">
                  {t('auth.sideHeroDescription')}
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-white/70">
                  <span className="rounded-full bg-white/10 px-3 py-1">
                    {t('auth.sideHeroPillTracking')}
                  </span>
                  <span className="rounded-full bg-white/10 px-3 py-1">
                    {t('auth.sideHeroPillPayouts')}
                  </span>
                  <span className="rounded-full bg-white/10 px-3 py-1">
                    {t('auth.sideHeroPillIds')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Terms and Privacy */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {modalContent === 'terms'
                  ? t('settings.termsOfService')
                  : t('settings.privacyPolicy')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition"
              >
                <X size={24} className="text-gray-600 dark:text-slate-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="[&_nav]:hidden">
                {modalContent === 'terms' ? (
                  <TermsOfServicePage />
                ) : (
                  <PrivacyPolicyPage />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
